import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../api/client";
import { Button, DataCard, Empty, Icon, PageTitle, Spinner, cx } from "../../../components/ui";
import { ChipTerm } from "../Beranda";
import { namaBulan } from "../logika";
import ProgresTL from "./ProgresTL";
import PanelKelasTL from "./PanelKelasTL";
import PengaturanTerm from "./PengaturanTerm";

// ─── Raport — halaman team leader (Fase 4) ───────────────────────────────────
//
// Rancangan §7.4. Dua tab: Progres (semua kelas raport cabang aktif, yang perlu
// ditindaklanjuti, dan panel rincian per kelas) serta Pengaturan term (periode & buka kunci).
// Layarnya disimpan di URL — /teamleader-raport, /teamleader-raport/kelas/201,
// /teamleader-raport/term (+ ?term=12) — jadi Back menutup panel lebih dulu dan refresh tetap
// di tempatnya. Team leader tak mengisi nilai; semua aturan diperiksa server.

const AKAR = "/teamleader-raport";

function bacaUrl() {
  const bagian = window.location.pathname.replace(/^\/+/, "").split("/");
  return {
    tab: bagian[1] === "term" ? "term" : "progres",
    classId: bagian[1] === "kelas" && Number(bagian[2]) > 0 ? Number(bagian[2]) : null,
    term: Number(new URLSearchParams(window.location.search).get("term")) || null
  };
}

function urlDari({ tab, classId, term }) {
  if (tab === "term") return `${AKAR}/term`;
  const p = classId ? `${AKAR}/kelas/${classId}` : AKAR;
  return term ? `${p}?term=${term}` : p;
}

const TAB = [["progres", "insights", "Progres"], ["term", "date_range", "Pengaturan term"]];

export default function RaportTLPage({ setToast }) {
  const [awal] = useState(bacaUrl);
  const [terms, setTerms] = useState(null);
  const [termId, setTermId] = useState(awal.term);
  const [lokasi, setLokasi] = useState({ tab: awal.tab, classId: awal.classId });
  const [progres, setProgres] = useState(null);
  const [galat, setGalat] = useState("");
  const [ulang, setUlang] = useState(0);
  const nomor = useRef(0);
  // Panel yang dibuka dari daftar menambah satu entri riwayat; menutupnya = mundur satu.
  const panelDidorong = useRef(false);

  useEffect(() => {
    let batal = false;
    api.getRaportTerms()
      .then((t) => {
        if (batal) return;
        setTerms(t);
        setTermId((id) => (id && t.term.some((x) => x.id === id) ? id : t.sekarang || (t.term.length ? t.term[t.term.length - 1].id : null)));
        setGalat("");
      })
      .catch((err) => { if (!batal) setGalat(err.message || "Raport belum bisa dimuat."); });
    return () => { batal = true; };
  }, [ulang]);

  const muatProgres = useCallback(() => {
    if (!termId) return;
    const ini = ++nomor.current;
    api.getRaportProgres(termId)
      .then((r) => { if (ini === nomor.current) { setProgres(r); setGalat(""); } })
      .catch((err) => { if (ini === nomor.current) { setProgres(null); setGalat(err.message || "Progres raport gagal dimuat."); } });
  }, [termId]);

  useEffect(() => { if (lokasi.tab === "progres") muatProgres(); }, [muatProgres, lokasi.tab]);

  useEffect(() => {
    const onPop = () => {
      const u = bacaUrl();
      panelDidorong.current = false;
      setLokasi({ tab: u.tab, classId: u.classId });
      if (u.term) setTermId(u.term);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const pergi = (tujuan, { ganti = false } = {}) => {
    const baru = { tab: tujuan.tab || "progres", classId: tujuan.classId || null };
    try {
      window.history[ganti ? "replaceState" : "pushState"]({}, "", urlDari({ ...baru, term: termId }));
    } catch (e) { /* abaikan */ }
    setLokasi(baru);
  };

  const bukaKelas = (id) => {
    pergi({ tab: "progres", classId: id });
    panelDidorong.current = true;
  };
  const tutupKelas = () => {
    if (panelDidorong.current) {
      panelDidorong.current = false;
      window.history.back();
    } else {
      pergi({ tab: "progres" }, { ganti: true });
    }
  };

  const pilihTerm = (id) => {
    if (id === termId) return;
    try { window.history.replaceState({}, "", urlDari({ ...lokasi, term: id })); } catch (e) { /* abaikan */ }
    setProgres(null);
    setTermId(id);
  };

  const pilihKelasUtama = async (studentId, classId) => {
    try {
      const r = await api.pilihKelasUtamaRaport(termId, studentId, classId);
      if (setToast) setToast({ type: "success", message: r.message || "Kelas utama ditetapkan." });
      muatProgres();
    } catch (err) {
      if (setToast) setToast({ type: "error", message: err.message || "Kelas utama gagal ditetapkan." });
    }
  };

  if (!terms && !galat) return <Spinner label="Memuat raport…" />;
  if (!terms) {
    return (
      <div className="anim-fade space-y-4">
        <Empty label={galat} />
        <div className="text-center">
          <Button variant="secondary" onClick={() => setUlang((n) => n + 1)}>
            <Icon name="refresh" />
            Coba lagi
          </Button>
        </div>
      </div>
    );
  }

  const term = terms.term.find((t) => t.id === termId);

  return (
    <div>
      <PageTitle
        title="Raport"
        subtitle="Pantau raport cabang ini: siapa yang mengisi, sejauh mana, dan raport yang sudah terbit."
      />

      <div className="anim-rise mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Bagian raport">
        {TAB.map(([k, ikon, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={lokasi.tab === k}
            onClick={() => pergi({ tab: k })}
            className={cx(
              "lift-press inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold",
              lokasi.tab === k
                ? "border-primary bg-primary text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-primary/60 hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
            )}
          >
            <Icon name={ikon} className="text-base" />
            {label}
          </button>
        ))}
      </div>

      {lokasi.tab === "term" ? (
        <PengaturanTerm setToast={setToast} onUbah={() => setUlang((n) => n + 1)} />
      ) : (
        <>
          <DataCard delay={1} className="mb-6 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Term</p>
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Pilih term">
              {terms.term.map((t) => (
                <ChipTerm key={t.id} t={t} aktif={t.id === termId} sekarang={t.id === terms.sekarang} onClick={() => pilihTerm(t.id)} />
              ))}
            </div>
            {term && (
              <p className="mt-3 text-sm text-slate-500">
                Bulan 1 {namaBulan(term.bulan[0])} · Bulan 2 {namaBulan(term.bulan[1])} · Bulan 3 {namaBulan(term.bulan[2])}
              </p>
            )}
          </DataCard>

          {galat ? (
            <Empty label={galat} />
          ) : !progres ? (
            <Spinner label="Memuat progres…" />
          ) : (
            <ProgresTL data={progres} onBukaKelas={bukaKelas} onPilihKelasUtama={pilihKelasUtama} />
          )}

          <PanelKelasTL
            classId={lokasi.classId}
            termId={termId}
            buka={!!lokasi.classId && !!termId}
            onTutup={tutupKelas}
            onUbah={muatProgres}
            setToast={setToast}
          />
        </>
      )}
    </div>
  );
}
